import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '@/shared/components/Header';
import MainNavigation from '@/shared/components/MainNavigation';
import TransferVerificationBanner from '@/domains/shop/components/TransferVerificationBanner';
import { useAuth } from '@/core/auth/useAuth';
import { isAdminAreaOnlyUser } from '@/core/permissions/adminAreaAccess';
import { OfflineProgressBar } from '@/core/offline/components/OfflineProgressBar';
import { useMutationQueue } from '@/contexts/MutationQueueContext';
import { AutoLogoutWarningDialog } from '@/shared/components/AutoLogoutWarningDialog';
import { OfflineIndicator } from '@/core/offline/components/OfflineIndicator';
import { CopyrightFooter } from '@/shared/components/CopyrightFooter';

export const MainLayout: React.FC = () => {
  const { hasRole } = useAuth();
  const { preloadProgress, isPreloading, isSyncing, triggerPreload, triggerSync, startPreload, isOnline } = useMutationQueue();

  // TODO: RIMUOVERE - Listener per pulsante sync temporaneo
  useEffect(() => {
    const handleForcePreload = () => {
      console.log('[MainLayout] Force preload triggered');
      triggerPreload();
      triggerSync();
    };

    window.addEventListener('force-preload', handleForcePreload);
    return () => window.removeEventListener('force-preload', handleForcePreload);
  }, [triggerPreload, triggerSync]);

  // Auto-start preload after 10 seconds delay to allow UI to load first
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isOnline && !isPreloading && preloadProgress.stage === 'idle') {
        console.log('[MainLayout] Starting deferred preload (10s delay)');
        startPreload();
      }
    }, 10000); // 10 secondi di delay

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  const location = useLocation();

  // Shop has its own header (ShopHeaderV2) — hide standard Header/Navigation to avoid overlap
  const isShopV2 = location.pathname.startsWith('/shop');

  // Utenti con soli ruoli dell'area amministrativa non vedono la navigazione Shop
  const isOnlyAmministrazione = isAdminAreaOnlyUser(hasRole);

  return (
    <>
      {/* Progress bar sottilissima per sync/preload - sempre visibile in alto */}
      <OfflineProgressBar
        progress={preloadProgress}
        isPreloading={isPreloading}
        isSyncing={isSyncing}
        isOnline={isOnline}
      />
      {!isShopV2 && <Header />}
      {!isShopV2 && !isOnlyAmministrazione && (
        <>
          <MainNavigation />
          <TransferVerificationBanner />
        </>
      )}
      <Outlet />
      <OfflineIndicator position="bottom" />
      <AutoLogoutWarningDialog />
      <CopyrightFooter />
    </>
  );
};
